import type { CodeableConcept } from '@medplum/fhirtypes'
import { codeableConcept } from '#src/codeableConcept.ts'
import {
  NUCC_PROVIDER_TAXONOMY_SYSTEM,
  PRACTITIONER_ROLE,
  SNOMED_CT_SYSTEM,
} from '#src/codeSystems.ts'

export const Role = {
  PCP: 'PCP',
  RN: 'RN',
  SW: 'SW',
  PT: 'PT',
  OT: 'OT',
  RT: 'RT',
  RD: 'RD',
  CM: 'CM',
  HC: 'HC',
  PC: 'PC',
  TR: 'TR',
  NP: 'NP',
  LVN: 'LVN',
  MA: 'MA',
  MD: 'MD',
  OUTREACH_SPECIALIST: 'OS',
  OUTREACH_ENROLLMENT_COORDINATOR: 'OEC',
  INTAKE_COORDINATOR: 'IC',
  FACILITIES: 'FC',
  PARTICIPANT_EXPERIENCE_SUPERVISOR: 'PES',
  ACM: 'ACM',
  CARE_OPS: 'CO',
  TECH: 'TE',
  CNA: 'CNA',
  QI_COMP_DIRECTOR: 'QICD',
  PTA: 'PTA',
} as const
export type Role = (typeof Role)[keyof typeof Role]

export const ROLES: Record<Role, CodeableConcept> = {
  [Role.PCP]: codeableConcept([
    {
      system: PRACTITIONER_ROLE,
      code: Role.PCP,
      display: 'Primary Care Provider',
    },
    {
      system: SNOMED_CT_SYSTEM,
      code: '446050000',
      display: 'PCP - Primary care physician',
    },
  ]),
  [Role.RN]: codeableConcept([
    {
      system: PRACTITIONER_ROLE,
      code: Role.RN,
      display: 'Nursing',
    },
    {
      system: SNOMED_CT_SYSTEM,
      code: '224535009',
      display: 'Registered nurse',
    },
  ]),
  [Role.SW]: codeableConcept([
    {
      system: PRACTITIONER_ROLE,
      code: Role.SW,
      display: 'Social Work',
    },
    {
      system: SNOMED_CT_SYSTEM,
      code: '224598009',
      display: 'Trained social worker counselor',
    },
  ]),
  [Role.PT]: codeableConcept([
    {
      system: PRACTITIONER_ROLE,
      code: Role.PT,
      display: 'Physical Therapy',
    },
    {
      system: SNOMED_CT_SYSTEM,
      code: '36682004',
      display: 'PT - Physiotherapist',
    },
  ]),
  [Role.OT]: codeableConcept([
    {
      system: PRACTITIONER_ROLE,
      code: Role.OT,
      display: 'Occupational Therapy',
    },
    {
      system: SNOMED_CT_SYSTEM,
      code: '80546007',
      display: 'OT - Occupational therapist',
    },
  ]),
  [Role.RT]: codeableConcept([
    {
      system: PRACTITIONER_ROLE,
      code: Role.RT,
      display: 'Recreational Therapy',
    },
    {
      system: NUCC_PROVIDER_TAXONOMY_SYSTEM,
      code: '225800000X',
      display: 'Recreation Therapist',
    },
  ]),
  [Role.RD]: codeableConcept([
    {
      system: PRACTITIONER_ROLE,
      code: 'RD',
      display: 'Nutrition',
    },
    {
      system: SNOMED_CT_SYSTEM,
      code: '159033005',
      display: 'Registered Dietitian',
    },
  ]),
  [Role.CM]: codeableConcept([
    {
      system: PRACTITIONER_ROLE,
      code: 'CM',
      display: 'Center Manager',
    },
    {
      system: SNOMED_CT_SYSTEM,
      code: '159481007',
      display: 'Practice manager',
    },
  ]),
  [Role.HC]: codeableConcept([
    {
      system: PRACTITIONER_ROLE,
      code: Role.HC,
      display: 'Home Care',
    },
    {
      system: SNOMED_CT_SYSTEM,
      code: '768730001',
      display: 'Home health aide',
    },
    {
      system: NUCC_PROVIDER_TAXONOMY_SYSTEM,
      code: '374U00000X',
      display: 'Home Health Aide',
    },
  ]),
  [Role.PC]: codeableConcept([
    {
      system: PRACTITIONER_ROLE,
      code: Role.PC,
      display: 'Personal Care',
    },
    {
      system: SNOMED_CT_SYSTEM,
      code: '768836001',
      display: 'Patient navigator',
    },
    {
      system: NUCC_PROVIDER_TAXONOMY_SYSTEM,
      code: '3747P1801X',
      display: 'Personal Care Attendant',
    },
  ]),
  [Role.TR]: codeableConcept([
    {
      system: PRACTITIONER_ROLE,
      code: Role.TR,
      display: 'Transportation',
    },
    {
      system: SNOMED_CT_SYSTEM,
      code: '308384000',
      display: 'Transportation',
    },
  ]),
  [Role.NP]: codeableConcept([
    {
      system: PRACTITIONER_ROLE,
      code: Role.NP,
      display: 'Nurse Practitioner',
    },
  ]),
  [Role.LVN]: codeableConcept([
    {
      system: PRACTITIONER_ROLE,
      code: Role.LVN,
      display: 'Licensed Vocational Nurse',
    },
  ]),
  [Role.MA]: codeableConcept([
    {
      system: PRACTITIONER_ROLE,
      code: Role.MA,
      display: 'Medical Assistant',
    },
  ]),
  [Role.MD]: codeableConcept([
    {
      system: PRACTITIONER_ROLE,
      code: Role.MD,
      display: 'Medical Director',
    },
  ]),
  [Role.OUTREACH_SPECIALIST]: codeableConcept([
    {
      system: PRACTITIONER_ROLE,
      code: Role.OUTREACH_SPECIALIST,
      display: 'Outreach',
    },
  ]),
  [Role.OUTREACH_ENROLLMENT_COORDINATOR]: codeableConcept([
    {
      system: PRACTITIONER_ROLE,
      code: Role.OUTREACH_ENROLLMENT_COORDINATOR,
      display: 'Outreach & Enrollment Coordinator',
    },
  ]),
  [Role.PARTICIPANT_EXPERIENCE_SUPERVISOR]: codeableConcept([
    {
      system: PRACTITIONER_ROLE,
      code: Role.PARTICIPANT_EXPERIENCE_SUPERVISOR,
      display: 'Participant Experience Supervisor',
    },
  ]),
  [Role.ACM]: codeableConcept([
    {
      system: PRACTITIONER_ROLE,
      code: Role.ACM,
      display: 'Assistant Center Manager',
    },
  ]),
  [Role.INTAKE_COORDINATOR]: codeableConcept([
    {
      system: PRACTITIONER_ROLE,
      code: Role.INTAKE_COORDINATOR,
      display: 'Intake Coordinator',
    },
  ]),
  [Role.FACILITIES]: codeableConcept([
    {
      system: PRACTITIONER_ROLE,
      code: Role.FACILITIES,
      display: 'Facilities',
    },
  ]),
  [Role.CARE_OPS]: codeableConcept([
    {
      system: PRACTITIONER_ROLE,
      code: Role.CARE_OPS,
      display: 'Care Ops',
    },
  ]),
  [Role.TECH]: codeableConcept([
    {
      system: PRACTITIONER_ROLE,
      code: Role.TECH,
      display: 'Tech',
    },
  ]),
  [Role.CNA]: codeableConcept([
    {
      system: PRACTITIONER_ROLE,
      code: Role.CNA,
      display: 'Certified Nursing Assistant',
    },
    {
      system: NUCC_PROVIDER_TAXONOMY_SYSTEM,
      code: '376G00000X',
      display: 'Nursing Assistant',
    },
  ]),
  [Role.QI_COMP_DIRECTOR]: codeableConcept([
    {
      system: PRACTITIONER_ROLE,
      code: Role.QI_COMP_DIRECTOR,
      display: 'QI/Comp Director',
    },
  ]),
  [Role.PTA]: codeableConcept([
    {
      system: PRACTITIONER_ROLE,
      code: Role.PTA,
      display: 'Physical Therapist Assistant',
    },
    {
      system: NUCC_PROVIDER_TAXONOMY_SYSTEM,
      code: '225200000X',
      display: 'Physical Therapist Assistant',
    },
  ]),
}
